import { useTransactionStore } from '@/stores/transactionStore'
import type { TransactionRecord } from '@/types/Transaction'
import type { CommodityType } from '@/types/CommodityType'
import { useCommodityTypeService } from '@/services/commodityTypeService'

export type TopCommodityRow = {
  commodityName: string
  commodityId: string
  category: string
  totalValue: number
  totalWeight: number
  transactionCount: number
  averagePricePerLb: number
}

/**
 * Executes the Top Commodities report.
 * Aggregates transaction items by commodity type, showing total value, weight, and transaction count.
 * @param transactionType - Filter by transaction type: 'purchased', 'sold', or 'combined'
 */
export async function executeTopCommodities(
  transactionType: 'purchased' | 'sold' | 'combined' = 'purchased',
): Promise<TopCommodityRow[]> {
  const transactionStore = useTransactionStore()
  await transactionStore.load()

  // Get transactions with items, filtered by transaction type
  let transactions: TransactionRecord[] = transactionStore.items.filter(
    (t) => !t.is_deleted && t.items && t.items.length > 0,
  )

  // Apply transaction type filter
  if (transactionType === 'purchased') {
    transactions = transactions.filter((t) => t.transaction_type === 'purchase')
  } else if (transactionType === 'sold') {
    transactions = transactions.filter((t) => t.transaction_type === 'sale')
  }
  // For 'combined', we use all transactions (no additional filtering)

  // Load commodity types for additional information
  const commodityTypes = await loadCommodityTypes()
  const commodityMap = new Map(commodityTypes.map((c) => [c.id, c]))

  // Aggregate data by commodity
  const commodityTotals = new Map<
    string,
    {
      commodityName: string
      commodityId: string
      category: string
      totalValue: number
      totalWeight: number
      transactionCount: number
      totalPricePerLb: number
      pricePerLbCount: number
    }
  >()

  for (const tx of transactions) {
    for (const item of tx.items) {
      const commodityId = item.commodity_type_id

      // Get commodity info from loaded commodity types
      const commodityType = commodityMap.get(commodityId)
      const commodityName = commodityType?.name || item.commodity_name || 'Unknown'
      const category = commodityType?.category || 'Unknown'

      const current = commodityTotals.get(commodityId) || {
        commodityName,
        commodityId,
        category,
        totalValue: 0,
        totalWeight: 0,
        transactionCount: 0,
        totalPricePerLb: 0,
        pricePerLbCount: 0,
      }

      current.totalValue += Number(item.total_price || 0)
      current.totalWeight += Number(item.weight?.net || 0)
      current.transactionCount += 1

      // Track price per lb for average calculation
      if (item.price_per_lb && item.price_per_lb > 0) {
        current.totalPricePerLb += Number(item.price_per_lb)
        current.pricePerLbCount += 1
      }

      commodityTotals.set(commodityId, current)
    }
  }

  // Convert to rows and calculate averages
  const rows: TopCommodityRow[] = Array.from(commodityTotals.values())
    .map((data) => ({
      commodityName: data.commodityName,
      commodityId: data.commodityId,
      category: data.category,
      totalValue: data.totalValue,
      totalWeight: data.totalWeight,
      transactionCount: data.transactionCount,
      averagePricePerLb: data.pricePerLbCount > 0 ? data.totalPricePerLb / data.pricePerLbCount : 0,
    }))
    .sort((a, b) => b.totalValue - a.totalValue) // Sort by total value descending

  return rows
}

async function loadCommodityTypes(): Promise<CommodityType[]> {
  try {
    const commodityTypeService = useCommodityTypeService()
    return await commodityTypeService.getActiveCommodityTypes()
  } catch (error) {
    console.error('Error loading commodity types:', error)
    return []
  }
}
